/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo } from "react";
import { styled } from "@iventis/styles";
import { LoginPanelContainer } from "@iventis/components/src/login-containers";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { PublicProject } from "@iventis/domain-model/model/publicProject";
import { Asset } from "@iventis/domain-model/model/asset";
import { AuthState, login } from "@iventis/permissions/src/auth.slice";
import { LoginComponent, IventisFooter, PoweredByIventisLogoComponent, LoadingComponent } from "@iventis/components";
import { assetsApi, getApiBaseUrl, getAppUrlForSubdomain, getIventisEnvironmentData, permissionsApi } from "@iventis/api/src/api";
import { MicroService } from "@iventis/api-helpers";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "@iventis/subscriptions";
import { getAssetSignature } from "@iventis/utilities";
import tile from "./assets/isometric-pink.png";
import { getPublicProject } from "./login-helpers";

const Login: React.FC = () => {
    const dispatch = useDispatch<ThunkDispatch<AuthState, typeof login, Action>>();
    const history = useHistory();
    const loginError = useSelector((state: { auth: AuthState }) => state.auth.loginError);
    const emailQueryValue = new URLSearchParams(window.location.search).get("email");

    const projectShortName = getIventisEnvironmentData().subDomain;
    // Required to show the logo on the login page
    const { data: project, status: publicProjectStatus } = useQuery<PublicProject>(["public-project", projectShortName], async () =>
        projectShortName == null ? null : getPublicProject(projectShortName)
    );
    const { data: projectLogoAsset, status: projectLogoAssetStatus } = useQuery<Asset>(
        ["project-logo", projectShortName],
        () => (project.logoId != null ? assetsApi.get<Asset>(`public-assets/${project?.logoId}`).then((res) => res.data) : null),
        { enabled: project != null }
    );

    const submitLocalLogin = async (email: string, password: string) => {
        await dispatch(login({ email, password }));
    };

    const ssoLoginClicked = async () => {
        window.location.replace(`${getApiBaseUrl(MicroService.PERMISSIONS)}authentication/saml-login?redirectUrl=${window.location.href}`);
    };

    const forgotPasswordClicked = () => {
        history.push("/forgotten-password");
    };

    const userEmailConfirmed = async (email: string) => {
        if (projectShortName != null) return; // Do not want to move to another short name if already in one.
        // Make request to back end regarding potential re-direct.
        const res = await permissionsApi.get<string>(`users/${email}/default-project`);
        if (res.data == null || res.data === "") return;
        const encodedEmail = encodeURIComponent(email);
        const redirect = `${getAppUrlForSubdomain(res.data)}/login?email=${encodedEmail}`;
        window.location.replace(redirect);
        // without this timeout, the location replace happens after the component state moves to the password page causing a flash of the password page.
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, 5000);
        });
    };

    const loading: boolean = useMemo(() => (publicProjectStatus === "loading" || projectLogoAssetStatus === "loading") && projectShortName != null, [
        publicProjectStatus,
        projectLogoAssetStatus,
    ]);

    return (
        <LoginBackground>
            <StyledPanel>
                {!loading && (
                    <>
                        {projectShortName != null && project?.logoId != null && <PoweredByIventisLogoComponent className="iventis-header" />}
                        <LoginPanelContainer id="middle">
                            <LoginComponent
                                ssoLoginClicked={ssoLoginClicked}
                                ssoEnabled={project?.sso ?? false}
                                loading={loading}
                                submitLocalLogin={submitLocalLogin}
                                forgottenPasswordClicked={forgotPasswordClicked}
                                signUpUrl={getIventisEnvironmentData().signUpUrl}
                                loginError={loginError}
                                userEmailConfirmed={userEmailConfirmed}
                                logoUrl={projectLogoAsset != null ? getAssetSignature(projectLogoAsset.assetUrl, projectLogoAsset.authoritySignature) : null}
                                initialEmailValue={emailQueryValue}
                            />
                        </LoginPanelContainer>
                        <IventisFooter />
                    </>
                )}
                {loading && <LoadingComponent />}
            </StyledPanel>
        </LoginBackground>
    );
};

export default Login;

const StyledPanel = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 400px;
    .iventis-header {
        padding-top: 50px;
    }
`;

export const LoginBackground = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(0deg, #ffff 70%, rgba(255, 255, 255, 0.5)), url(${tile});
`;
